import { Divider, Stack, Typography } from '@mui/material';
import { Card } from 'components';
import React, { Fragment } from 'react';
import { useCampaign } from 'hooks';
import { TargetingGuidanceConfig } from 'types';
import {
  ageOptionsMap,
  companySizeOptionsMap,
  genderOptionsMap,
  industryOptionsMap,
  languageOptionsMap,
} from './constants';
import { TargetingOverviewBlock } from './TargetingOverviewBlock';
import { defaultTargetingGuidanceConfig } from 'shared';

const getStructuredTargetingGuidance = (targetingGuidance: TargetingGuidanceConfig) => {
  return (
    <>
      {targetingGuidance?.gender ? (
        <Typography variant="h4" fontWeight="normal">
          <b>Geschlecht:&nbsp;</b>
          {targetingGuidance?.gender.length === 0
            ? 'Alle'
            : targetingGuidance?.gender.map((value) => genderOptionsMap?.[value]).join(', ')}
        </Typography>
      ) : null}
      {targetingGuidance?.age ? (
        <Typography variant="h4" fontWeight="normal">
          <b>Altersspanne:&nbsp;</b>
          {targetingGuidance?.age.length === 0
            ? 'Alle'
            : targetingGuidance?.age.map((value) => ageOptionsMap?.[value]).join(', ')}
        </Typography>
      ) : null}
      {targetingGuidance?.languages ? (
        <Typography variant="h4" fontWeight="normal">
          <b>Sprache:&nbsp;</b>
          {targetingGuidance?.languages.length === 0
            ? 'Alle'
            : targetingGuidance?.languages.map((value) => languageOptionsMap?.[value]).join(', ')}
        </Typography>
      ) : null}
      {targetingGuidance?.locations ? (
        <Typography variant="h4" fontWeight="normal">
          <b>Standorte:&nbsp;</b>
          {targetingGuidance?.locations.length === 0
            ? 'Alle'
            : targetingGuidance?.locations?.map((location) => location.canonicalName).join(', ')}
        </Typography>
      ) : null}
      {targetingGuidance?.interests ? (
        <Typography variant="h4" fontWeight="normal">
          <b>Interessen:&nbsp;</b>
          {targetingGuidance?.interests.length === 0 ? 'Alle' : targetingGuidance?.interests.join(', ')}
        </Typography>
      ) : null}
      {targetingGuidance?.industry ? (
        <Typography variant="h4" fontWeight="normal">
          <b>Branche:&nbsp;</b>
          {targetingGuidance?.industry.length === 0
            ? 'Alle'
            : targetingGuidance?.industry.map((value) => industryOptionsMap?.[value]).join(', ')}
        </Typography>
      ) : null}
      {targetingGuidance?.companySize ? (
        <Typography variant="h4" fontWeight="normal">
          <b>Unternehmensgrösse:&nbsp;</b>
          {targetingGuidance?.companySize.length === 0
            ? 'Alle'
            : targetingGuidance?.companySize.map((value) => companySizeOptionsMap?.[value]).join(', ')}
        </Typography>
      ) : null}
    </>
  );
};

export const OverviewTargeting = () => {
  const { getCampaignQuery } = useCampaign();
  const campaign = getCampaignQuery.data?.campaign;
  const targetingGuidance = campaign?.targetingGuidance;
  const targetingGuidanceConfig = campaign?.targetingGuidanceConfig;
  const targetingGuidanceFormat = campaign?.targetingGuidanceFormat;
  const targetingGuidanceMode = campaign?.targetingGuidanceMode;

  const isTargetingPerWebsiteEmpty =
    targetingGuidanceMode === 'perWebsite' &&
    campaign?.websites?.every(
      (website) =>
        (website.targetingGuidance === '' || !website.targetingGuidance) &&
        (!website.targetingGuidanceConfig || Object.keys(website.targetingGuidanceConfig).length === 0)
    );

  const isGlobalTargetingFulfilled =
    targetingGuidanceMode === 'global' &&
    ((targetingGuidanceFormat === 'text' && targetingGuidance && targetingGuidance !== '') ||
      (targetingGuidanceFormat === 'json' &&
        targetingGuidanceConfig &&
        Object.keys(targetingGuidanceConfig).length > 0));

  return (
    <Stack spacing={5} mt={5}>
      <Typography variant="h2">Zielgruppen</Typography>
      <Card sx={{ maxWidth: { xs: '100%', sm: '70%' } }}>
        <TargetingOverviewBlock
          title="Optimierte Zielgruppen aktiv"
          description={
            <Typography variant="body1">
              Die optimierten Zielgruppen sind automatisch für alle Kampagnen aktiviert. Es nutzt die Informationen
              deiner Kampagnen-Website und Creatives, um Zielgruppen zu finden, die deine Kampagnenziele effizient
              erreichen können.
            </Typography>
          }
          tooltipText={
            <Stack spacing={2}>
              <Typography variant="body2">
                Optimierte Zielgruppen helfen dir, deine Kampagnenziele effizient zu erreichen, indem sie Daten von
                deiner Kampagnen-Website und den Creatives nutzen.
              </Typography>
              <Typography variant="body2">
                Berechnet werden nur Interaktionen wie Klicks auf deine Banner oder Engagements auf der
                Kampagnen-Website.
              </Typography>
            </Stack>
          }
        />
      </Card>
      {isGlobalTargetingFulfilled ? (
        <Card sx={{ maxWidth: { xs: '100%', sm: '70%' } }}>
          <TargetingOverviewBlock
            title="Eigene Zielgruppe wurde zusätzlich hinzugefügt"
            description={
              <>
                {campaign?.websites?.length !== undefined && campaign?.websites?.length > 1 ? (
                  <Typography display="inline" variant="subtitle1" fontWeight={700}>
                    Gleiche Zielgruppe für alle Webseiten
                  </Typography>
                ) : null}
                {targetingGuidanceFormat === 'text' ? (
                  <Typography variant="body1">{targetingGuidance}</Typography>
                ) : (
                  getStructuredTargetingGuidance(targetingGuidanceConfig || defaultTargetingGuidanceConfig)
                )}
              </>
            }
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Zusätzlich zu den optimierten Zielgruppen hast du hier eine eigene Zielgruppe definiert. Die
                  optimierten Zielgruppen bleiben weiterhin aktiv.
                </Typography>
                <Typography variant="body2">
                  Diese kostenlose Zusatzoption hilft dir, deine Kampagnenziele noch besser zu erreichen.
                </Typography>
              </Stack>
            }
          />
        </Card>
      ) : null}
      {!['none', 'global'].includes(targetingGuidanceMode as string) && !isTargetingPerWebsiteEmpty ? (
        <Card sx={{ maxWidth: { xs: '100%', sm: '70%' } }}>
          <TargetingOverviewBlock
            title="Eigene Zielgruppe wurde zusätzlich hinzugefügt"
            description={campaign?.websites.map((website, index) => {
              return (
                <Fragment key={website.id}>
                  <Stack mb={2} spacing={2}>
                    <Typography variant="h3">{website.url}</Typography>
                    {targetingGuidanceFormat === 'text' ? (
                      <Typography variant="body1">{website.targetingGuidance || ''}</Typography>
                    ) : (
                      getStructuredTargetingGuidance(website.targetingGuidanceConfig || defaultTargetingGuidanceConfig)
                    )}
                  </Stack>
                  {index < campaign.websites.length - 1 ? <Divider sx={{ mt: 5 }} /> : null}
                </Fragment>
              );
            })}
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Zusätzlich zu den optimierten Zielgruppen hast du hier eine eigene Zielgruppe definiert. Die
                  optimierten Zielgruppen bleiben weiterhin aktiv.
                </Typography>
                <Typography variant="body2">
                  Diese kostenlose Zusatzoption hilft dir, deine Kampagnenziele noch besser zu erreichen.
                </Typography>
              </Stack>
            }
          />
        </Card>
      ) : null}
    </Stack>
  );
};
